import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { CalendarClock, RefreshCw, BellPlus, ExternalLink, Download, Printer } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DIAG_ORDER, DIAG_META, DiagBadge, fmtMoney, techNamesText, techTooltip } from './BillingDiagnosticGroups';
import BillingFollowUpDialog from './BillingFollowUpDialog';
import BillingCreateSheet from './BillingCreateSheet';
import { FollowUpRowActions, custUrl, jobUrl } from './FollowUpRowActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = 'all';
const QUICK = [['open', 'All Open'], ['this_week', 'This Week'], ['overdue', 'Overdue Follow-Ups'], ['none', 'No Follow-Up Set']];
const QB_LABELS = { needs_review: 'Needs Review', ready_for_qb: 'Ready for QB', entered_in_qb: 'Entered in QB', paid: 'Paid', on_hold: 'On Hold' };
const SOURCE_LABELS = { invoice: 'Invoice', invoice_name_only: 'Invoice (name only)', manual_assignment: 'Office assignment', completion: 'Completion', completion_submitted_by: 'Completion (submitted by)', shift: 'Shift', shift_claimed: 'Shift (claimed)', none: 'None' };
export const techNames = (r) => (r.tech_attribution?.technicians || []).map((t) => t.name);
const techTitle = (r) => {
  const a = r.tech_attribution;
  if (!a) return '';
  return `Source: ${SOURCE_LABELS[a.source] || a.source}${a.technician_id ? ` · id ${a.technician_id}` : ''}${a.detail ? ` · ${a.detail}` : ''}`;
};

export const EXPORT_COLUMNS = ['Customer', 'Service Date', 'Tech', 'Invoice / Job #', 'Amount', 'QB Status', 'Diagnostic Category', 'Follow-Up Reason', 'Follow-Up Date', 'Assigned To', 'Note', 'Resolved'];
export const exportRow = (r) => {
  const fu = r.followup;
  return [
    r.customer_name || 'Unknown',
    r.service_date || '',
    techNamesText(r),
    r.record_type === 'invoice' ? (r.invoice_number || r.id) : (r.work_order_number ? `WO ${r.work_order_number}` : 'Job — no invoice'),
    r.amount == null ? '$ unknown' : Number(r.amount).toFixed(2),
    r.qb_status ? (QB_LABELS[r.qb_status] || r.qb_status) : '',
    r.categories.length ? r.categories.map((c) => DIAG_META[c]?.label || c).join('; ') : 'Manual',
    fu?.reason_label || '',
    fu?.due_date || '',
    fu ? (fu.assigned_to_name || 'Unassigned') : '',
    fu?.note || '',
    fu ? (fu.resolved ? 'Yes' : 'No') : '',
  ];
};
const csvCell = (v) => { const t = String(v ?? ''); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
export const exportCsv = (rows, label) => {
  const csv = [EXPORT_COLUMNS, ...rows.map(exportRow)].map((r) => r.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `billing-follow-up-${label}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};
const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
export const printRows = (rows, title) => {
  const w = window.open('', '_blank');
  if (!w) { toast.error('Allow pop-ups to print'); return; }
  w.document.write(`<html><head><title>${esc(title)}</title><style>body{font-family:Arial,sans-serif;font-size:11px;margin:16px}h2{margin:0 0 4px}p{margin:0 0 10px;color:#555}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;vertical-align:top}th{background:#eee}</style></head><body><h2>${esc(title)}</h2><p>${rows.length} record(s) · printed ${esc(new Date().toLocaleString())}</p><table><thead><tr>${EXPORT_COLUMNS.map((c) => `<th>${esc(c)}</th>`).join('')}</tr></thead><tbody>${rows.map((r) => `<tr>${exportRow(r).map((v) => `<td>${esc(v)}</td>`).join('')}</tr>`).join('')}</tbody></table></body></html>`);
  w.document.close(); w.focus(); w.print();
};

const STATE_STYLE = { overdue: 'text-red-300', this_week: 'text-[#D4AF37]', scheduled: 'text-emerald-300', none: 'text-[#525252]' };

const FilterSelect = ({ testid, value, onChange, placeholder, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="w-[170px] h-8 bg-[#0A0A0A] border-[#262626] text-white text-xs"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="bg-[#141414] border-[#262626]">
      <SelectItem value={ALL} className="text-white">{placeholder}: All</SelectItem>
      {options.map(([v, l]) => <SelectItem key={v} value={v} className="text-white">{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const WeeklyBillingFollowUpTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quick, setQuick] = useState('open');
  const [f, setF] = useState({ assignee: ALL, tech: ALL, qb: ALL, cat: ALL });
  const [editing, setEditing] = useState(null);
  const [jobCtx, setJobCtx] = useState(null);
  const openCreateInvoice = async (r) => {
    try {
      const { data: c } = await axios.get(`${API}/admin/billing/job-context/${r.appointment_id}`, { withCredentials: true });
      if (!c.can_create_invoice) { toast.error('You do not have permission to create invoices'); return; }
      setJobCtx(c);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load the job'); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/billing/followup-queue`, { withCredentials: true });
      setData(d);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load follow-up queue'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const uniq = (fn) => [...new Set(rows.map(fn).filter(Boolean))].sort();
  const assignees = uniq((r) => r.followup && !r.followup.resolved && r.followup.assigned_to_name);
  const techs = [...new Set(rows.flatMap(techNames))].sort();
  const hasNoTech = rows.some((r) => techNames(r).length === 0);

  const filtered = useMemo(() => rows.filter((r) => {
    if (quick === 'this_week' && !['this_week', 'overdue'].includes(r.followup_state)) return false;
    if (quick === 'overdue' && r.followup_state !== 'overdue') return false;
    if (quick === 'none' && r.followup_state !== 'none') return false;
    if (f.assignee !== ALL && (r.followup?.resolved || r.followup?.assigned_to_name !== f.assignee)) return false;
    if (f.tech === '__unassigned__' && techNames(r).length > 0) return false;
    if (f.tech !== ALL && f.tech !== '__unassigned__' && !techNames(r).includes(f.tech)) return false;
    if (f.qb !== ALL && r.qb_status !== f.qb) return false;
    if (f.cat !== ALL && !r.categories.includes(f.cat)) return false;
    return true;
  }), [rows, quick, f]);

  const counts = useMemo(() => ({
    open: rows.length,
    this_week: rows.filter((r) => ['this_week', 'overdue'].includes(r.followup_state)).length,
    overdue: rows.filter((r) => r.followup_state === 'overdue').length,
    none: rows.filter((r) => r.followup_state === 'none').length,
  }), [rows]);

  return (
    <div data-testid="weekly-followup-tab" className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-white font-['Outfit'] text-xl font-bold flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[#D4AF37]" /> Weekly Billing Follow-Up</h3>
          <p className="text-[#A3A3A3] text-xs mt-1 max-w-3xl">
            Every record the Hub detected as needing billing attention, plus anything the office scheduled a follow-up on.
            QB Status = where it is in QuickBooks. Category = what is wrong. Follow-Up = why we revisit, when, and who owns it.
            {data && <> Week: <span className="text-white">{data.week_start} → {data.week_end}</span> (Sat–Fri).</>}
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="followup-export-csv" disabled={!filtered.length} onClick={() => exportCsv(filtered, quick)} className="text-xs font-bold px-3 py-1.5 rounded border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 inline-flex items-center gap-1 disabled:opacity-40"><Download className="w-3.5 h-3.5" /> Export Current View (CSV)</button>
          <button data-testid="followup-print" disabled={!filtered.length} onClick={() => printRows(filtered, `Weekly Billing Follow-Up — ${QUICK.find(([k]) => k === quick)?.[1] || ''}`)} className="text-xs font-bold px-3 py-1.5 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 disabled:opacity-40"><Printer className="w-3.5 h-3.5" /> Print</button>
          <button data-testid="followup-refresh" onClick={load} className="text-xs font-bold px-3 py-1.5 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="followup-quick-filters">
        {QUICK.map(([k, l]) => (
          <button key={k} data-testid={`followup-filter-${k}`} onClick={() => setQuick(k)} className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${quick === k ? 'border-[#D4AF37] text-[#0A0A0A] bg-[#D4AF37]' : 'border-[#3f3f3f] text-[#A3A3A3] hover:text-white'}`}>
            {l} <span className="opacity-70">({counts[k]})</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <FilterSelect testid="followup-filter-assignee" value={f.assignee} onChange={(v) => setF({ ...f, assignee: v })} placeholder="Assigned To" options={assignees.map((a) => [a, a])} />
        <FilterSelect testid="followup-filter-tech" value={f.tech} onChange={(v) => setF({ ...f, tech: v })} placeholder="Tech" options={[...techs.map((t) => [t, t]), ...(hasNoTech ? [['__unassigned__', 'Unassigned']] : [])]} />
        <FilterSelect testid="followup-filter-qb" value={f.qb} onChange={(v) => setF({ ...f, qb: v })} placeholder="QB Status" options={Object.entries(QB_LABELS)} />
        <FilterSelect testid="followup-filter-category" value={f.cat} onChange={(v) => setF({ ...f, cat: v })} placeholder="Category" options={DIAG_ORDER.map((k) => [k, DIAG_META[k].label])} />
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
        <table className="w-full text-xs" data-testid="followup-table">
          <thead className="text-[10px] uppercase tracking-wider text-[#A3A3A3] border-b border-[#262626]">
            <tr>{['Customer', 'Service Date', 'Tech', 'Invoice / Job #', 'Amount', 'QB Status', 'Category', 'Follow-Up Reason', 'Follow-Up Date', 'Assigned To', 'Note', 'Resolved', ''].map((h) => <th key={h} className="text-left px-3 py-2 font-bold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={13} className="text-center text-[#A3A3A3] py-8" data-testid="followup-empty">{loading ? 'Loading…' : 'Nothing in this view.'}</td></tr>}
            {filtered.map((r) => {
              const fu = r.followup && !r.followup.resolved ? r.followup : null;
              const done = r.followup?.resolved ? r.followup : null;
              return (
                <tr key={`${r.record_type}-${r.id}`} data-testid={`followup-row-${r.id}`} className="border-b border-[#1f1f1f] hover:bg-[#1a1a1a] align-top">
                  <td className="px-3 py-2 text-white font-medium min-w-[150px]">
                    {custUrl(r) ? <Link data-testid={`followup-customer-link-${r.id}`} to={custUrl(r)} className="hover:text-[#D4AF37] underline decoration-dotted underline-offset-2">{r.customer_name || 'Unknown'}</Link> : (r.customer_name || 'Unknown')}
                    {r.issues?.[0] && <p className="text-[10px] text-[#7f7f7f] mt-0.5 line-clamp-2" title={r.issues.join(' · ')}>{r.issues[0]}</p>}
                  </td>
                  <td className="px-3 py-2 text-[#D4D4D4] whitespace-nowrap">{r.service_date || '—'}</td>
                  <td className="px-3 py-2 text-white font-semibold min-w-[110px]" title={techTitle(r)} data-testid={`followup-tech-${r.id}`} data-tech-source={r.tech_attribution?.source}>{techNamesText(r)}</td>
                  <td className="px-3 py-2 font-mono text-[#A3A3A3] whitespace-nowrap">
                    {r.record_type === 'invoice'
                      ? <Link data-testid={`followup-open-${r.id}`} to={`/admin/invoices?open=${r.id}`} className="hover:text-[#D4AF37] inline-flex items-center gap-1">{r.invoice_number || `…${r.id.slice(-6)}`}<ExternalLink className="w-3 h-3" /></Link>
                      : (jobUrl(r) ? <Link data-testid={`followup-job-link-${r.id}`} to={jobUrl(r)} className="hover:text-[#D4AF37] inline-flex items-center gap-1">{r.work_order_number ? `WO ${r.work_order_number}` : 'Job — no invoice'}<ExternalLink className="w-3 h-3" /></Link> : <span>{r.work_order_number ? `WO ${r.work_order_number}` : 'Job — no invoice'}</span>)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-[#D4AF37] font-bold" data-testid={`followup-amount-${r.id}`}>{r.amount == null ? <span className="text-[#7f7f7f] font-normal">$ unknown</span> : fmtMoney(r.amount)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-[#D4D4D4]">{r.qb_status ? (QB_LABELS[r.qb_status] || r.qb_status) : '—'}</td>
                  <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{r.categories.length ? r.categories.map((c) => <DiagBadge key={c} cat={c} />) : <span className="text-[#525252]">Manual</span>}</div></td>
                  <td className="px-3 py-2 text-white whitespace-nowrap">{fu?.reason_label || (done ? <span className="text-[#7f7f7f]">{done.reason_label}</span> : <span className="text-[#525252]">—</span>)}</td>
                  <td className={`px-3 py-2 whitespace-nowrap font-bold ${STATE_STYLE[r.followup_state]}`} data-testid={`followup-date-${r.id}`}>{fu ? <>{fu.due_date}{r.followup_state === 'overdue' && ' · overdue'}</> : (done ? <span className="text-[#7f7f7f] font-normal">{done.due_date}</span> : '—')}</td>
                  <td className="px-3 py-2 text-[#D4D4D4] whitespace-nowrap">{fu?.assigned_to_name || (fu ? 'Unassigned' : '—')}</td>
                  <td className="px-3 py-2 text-[#A3A3A3] max-w-[200px]"><span className="line-clamp-2" title={(fu || done)?.note}>{(fu || done)?.note || ''}</span></td>
                  <td className="px-3 py-2 whitespace-nowrap" data-testid={`followup-resolved-${r.id}`}>{done ? <span className="text-green-400 font-bold">Yes</span> : (fu ? <span className="text-[#A3A3A3]">No</span> : '')}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <button data-testid={`followup-edit-${r.id}`} onClick={() => setEditing(r)} className="text-[11px] font-bold px-2 py-1 rounded border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 inline-flex items-center gap-1 whitespace-nowrap"><BellPlus className="w-3 h-3" /> {fu ? 'Edit' : 'Follow Up'}</button>
                      <FollowUpRowActions r={r} onFollowUp={setEditing} onCreateInvoice={openCreateInvoice} onChanged={load} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <BillingFollowUpDialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        recordType={editing?.record_type}
        recordId={editing?.id}
        followup={editing?.followup}
        title={editing ? `${editing.customer_name || 'Unknown'} · ${editing.service_date || ''}` : ''}
        onSaved={load}
      />
      {jobCtx && (
        <BillingCreateSheet key={jobCtx.appointment_id} type="invoice" trigger={false} open={!!jobCtx} jobContext={jobCtx}
          onOpenChange={(o) => { if (!o) setJobCtx(null); }}
          onCreated={() => { setJobCtx(null); toast.success('Invoice created — the row now reclassifies from the live billing diagnostics'); load(); }} />
      )}
    </div>
  );
};

export default WeeklyBillingFollowUpTab;
